import { FC, Component } from 'react';
import { IOApi, ApiFile, ApiHistory } from '../api/io';
import Table from './Table';
import { Button, Segment, Dimmer, Loader, TableProps as TableImplProps } from 'semantic-ui-react';
import FileWindow from './FileWindow';
import HistoryWindow from './HistoryWindow';
import Uploader, { UploaderProps } from './Uploader';
import MessageWindow from './MessageWindow';
import { envColor, confirmationWindowByEnvs } from '../common'


function requestSubmitted(o: any): boolean {
    return typeof o === 'object' && o.hasOwnProperty('request_submitted');
}


function hasError(o: any): boolean {
    return typeof o === 'object' && o.hasOwnProperty('error');
}


export interface IOApiTableActions {
    load(): Promise<void>;
    write(files: File[]): Promise<void>;
    read(path: string): void;
    historyFile(path: string): void;
    remove(row: any): void;
}


interface IOApiTableProps extends TableImplProps {
    api: IOApi;
    path?: string;
    uploader?: FC<UploaderProps>;
    onInit?(actions: IOApiTableActions): void;
    items?: any[];
    withoutTopButtons?: boolean;
}

interface IOApiTableState {
    tableLoading: boolean;
    fileLoading: boolean;
    rows: any[];
    file?: ApiFile;
    historyFile?: ApiHistory;
    message: string;
}

class IOApiTable extends Component<IOApiTableProps, IOApiTableState> {
    constructor(props: IOApiTableProps) {
        super(props);
        this.state = {
            tableLoading: false,
            fileLoading: false,
            rows: [],
            file: undefined,
            historyFile: undefined,
            message: ''
        }
    }

    closeFile() {
        this.setState({ file: undefined });
    }

    closeHistory() {
        this.setState({ historyFile: undefined });
    }

    async openFile(path: string[]) {
        const { api } = this.props;
        this.setState({ fileLoading: true })
        const file = await api.read(path[0], path[1]);
        this.setState({ file, fileLoading: false });
    }

    async load() {
        const { api, path, items } = this.props;

        if (items && items.length > 0) {
            this.setState({ rows: items })
            return ;
        }

        this.setState({ tableLoading: true });
        return api.index(path ?? '')
            .then(rows => {
                this.setState({ rows: rows ?? [] });
            })
            .finally(() => this.setState({ tableLoading: false }))
    };

    async upload(files: File[]) {
        const { api } = this.props;
        this.setState({ tableLoading: true })
        const response = await api.write(files);

        if (requestSubmitted(response)) {
            this.setState({ message: 'request submitted', tableLoading: false });
        }
        else if (hasError(response)) {
            this.setState({ message: response['error'], tableLoading: false });
        }
        else {
            this.load();
        }
    };

    async download(path: string) {
        const { api } = this.props;
        this.setState({ fileLoading: true })
        const file = await api.read(path);
        this.setState({ file, fileLoading: false });
    };

    async get_history(path: string) {
        const { api } = this.props;
        this.setState({ fileLoading: true })
        const historyFile = await api.history(path);
        this.setState({ historyFile, fileLoading: false });
    };


    async remove(row: any) {
        // const { rows } = this.state;
        // rows.splice(rows.findIndex(r => JSON.stringify(r) === JSON.stringify(row)), 1);
        // this.setState({ rows: [...rows] });

        const confirmationResult = confirmationWindowByEnvs(`Please confirm "Remove" action`, ['prod', 'ppe']);
        if (confirmationResult == true || confirmationResult == undefined) {
            this.setState({ tableLoading: true });
            const response = await this.props.api.remove(row);

            if (requestSubmitted(response)) {
                this.setState({ message: 'request submitted', tableLoading: false });
            }
            else {
                this.load();
            }
        }
    };

    componentDidMount() {
        const { onInit } = this.props;

        if (onInit) {
            const { load, upload, download, get_history, remove } = this;
            onInit({
                load: load.bind(this),
                write: upload.bind(this),
                read: download.bind(this),
                historyFile: get_history.bind(this),
                remove: remove.bind(this)
            });
        }

        this.load();
    }

    renderUploader() {
        const { api, uploader } = this.props;

        if (!api.isAllowed('write')) {
            return false;
        }

        const upload = this.upload.bind(this);

        if (uploader) {
            return uploader({ onUpload: upload });
        }
        else {
            return <Uploader onUpload={upload} />
        }
    }

    render() {
        const { api, path, uploader, children, onInit, withoutTopButtons, ...tableProps } = this.props;
        const { fileLoading, tableLoading, file, historyFile, rows, message } = this.state;

        return (
            <div>
                <Dimmer active={fileLoading}><Loader/></Dimmer>

                {message.length > 0 &&
                    <MessageWindow
                        message={message}
                        onClose={() => this.setState({ message: ''})}
                    />
                }

                {file &&
                    <FileWindow
                        file={file}
                        onClose={this.closeFile.bind(this)}
                    />
                }

                {historyFile &&
                    <HistoryWindow
                        currentApi={api}
                        file={historyFile}
                        onClose={this.closeHistory.bind(this)}
                        onFile={this.openFile.bind(this)}
                        onRestore={this.load.bind(this)}
                    />
                }

                {withoutTopButtons ? (
                    tableLoading ?
                        <Dimmer active inverted><Loader /></Dimmer> :
                        <Table rows={rows} {...tableProps}>
                            {children}
                        </Table>
                ) : (
                    <Segment>
                        <Button
                            circular
                            as="label"
                            icon="sync"
                            color={envColor}
                            onClick={this.load.bind(this)}
                            style={{ marginRight: "15px" }}
                            type="button"
                        />

                        {this.renderUploader()}

                        {tableLoading ?
                            <Dimmer active inverted><Loader /></Dimmer> :
                            <Table rows={rows} {...tableProps}>
                                {children}
                            </Table>
                        }
                    </Segment>
                )}

            </div>
        );
    }
};


export default IOApiTable;
